import { useMemo } from "react";

import {
  useGetCompanySpecificIndustryList,
  useGetNationalityList,
  useGetTaxRateList,
} from "@/api/services/global/master";
import { TOption } from "@/types/common";

/**
 * 商品設定 master data (国籍 / 分野 / 税率), sourced from the global master API. Option `value`s are
 * stringified ids (the product form keeps ids as strings); the *Name helpers resolve an id back to
 * its label for read-only views. All three queries are react-query cached + deduped across callers.
 */
export const useProductMasters = () => {
  const { data: nationalities } = useGetNationalityList();
  const { data: industries } = useGetCompanySpecificIndustryList();
  const { data: taxRates } = useGetTaxRateList();

  return useMemo(() => {
    const nationalityOptions: TOption[] = nationalities
      .filter((item) => !item.isHidden)
      .map((item) => ({ value: String(item.id), label: item.nationalityName }));
    const industryOptions: TOption[] = industries
      .filter((item) => !item.isHidden)
      .map((item) => ({ value: String(item.id), label: item.companySpecificIndustry }));
    const taxRateOptions: TOption[] = taxRates
      .filter((item) => !item.isHidden)
      .map((item) => ({ value: String(item.id), label: item.displayName }));

    const nationalityLabelMap = new Map<string, string>(nationalityOptions.map((option) => [String(option.value), option.label]));
    const industryLabelMap = new Map<string, string>(industryOptions.map((option) => [String(option.value), option.label]));
    const taxRateLabelMap = new Map<string, string>(taxRateOptions.map((option) => [String(option.value), option.label]));

    const labelOf = (labelMap: Map<string, string>, id: number | string | null | undefined): string =>
      id == null ? "" : labelMap.get(String(id)) ?? "";

    return {
      nationalityOptions,
      industryOptions,
      taxRateOptions,
      getNationalityName: (id: number | string | null | undefined) => labelOf(nationalityLabelMap, id),
      getIndustryName: (id: number | string | null | undefined) => labelOf(industryLabelMap, id),
      getTaxRateDisplayName: (id: number | string | null | undefined) => labelOf(taxRateLabelMap, id),
    };
  }, [nationalities, industries, taxRates]);
};
